import React, { useEffect, useImperativeHandle, forwardRef, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css"; // 스타일 적용
import ImageResize from "quill-image-resize"; // 이미지 리사이즈 추가

// 이미지 리사이즈 모듈 등록
if (!Quill.imports["modules/ImageResize"]) {
  Quill.register("modules/ImageResize", ImageResize);
}

const Editor = forwardRef(({ readOnly = false, initialHTML, placeholder = "" }, ref) => {
  // 내부 상태로 에디터 컨텐츠 관리
  const [content, setContent] = useState(initialHTML || "");
  const containerRef = useRef(null);
  const quillInstance = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // 이미 인스턴스가 있으면 완전히 정리
    if (quillInstance.current) {
      // 기존 인스턴스 연결 해제
      quillInstance.current.off("selection-change");
      quillInstance.current.off("text-change");

      // 컨테이너 내용 완전히 비우기
      containerRef.current.innerHTML = "";

      // 참조 제거
      quillInstance.current = null;
    }

    // 에디터 컨테이너와 툴바 컨테이너 분리
    const editorContainer = document.createElement("div");
    editorContainer.className = "editor-content";
    containerRef.current.appendChild(editorContainer);

    // Quill 인스턴스 생성 - 내부적으로 툴바 생성
    quillInstance.current = new Quill(editorContainer, {
      theme: "snow",
      readOnly,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ color: [] }, { background: [] }],
          ["link", "image"],
        ],
        ImageResize: {
          parchment: Quill.import("parchment"),
        },
      },
      placeholder: placeholder || "내용을 입력하세요.",
    });

    // 텍스트 변경 이벤트 리스너
    quillInstance.current.on("text-change", (delta) => {
      // 내부 상태 업데이트
      setContent(quillInstance.current.root.innerHTML);
    });

    // HTML 내용이 있으면 HTML로 설정
    if (initialHTML) {
      quillInstance.current.clipboard.dangerouslyPasteHTML(initialHTML);
    }

    // 에디터 높이 조정
    editorContainer.style.height = "calc(100% - 30px)"; // 툴바 공간 고려해서 높이 조정

    // 중요: 툴바 위치 조정 (상단에 고정)
    const toolbar = containerRef.current.querySelector(".ql-toolbar");
    if (toolbar) {
      toolbar.style.position = "sticky";
      toolbar.style.top = "0";
      toolbar.style.zIndex = "1";
    }

    // 반응형 툴바 높이 조정을 위한 함수
    const adjustToolbarHeight = () => {
      if (toolbar && editorContainer) {
        // editorContainer의 너비를 기준으로 판단
        if (editorContainer.clientWidth < 500) {
          toolbar.style.height = "60px";
          // 툴바 높이가 변경됨에 따라 에디터 높이도 조정
          editorContainer.style.height = "calc(100% - 60px)";
        } else {
          toolbar.style.height = ""; // 기본값으로 복원
          editorContainer.style.height = "calc(100% - 30px)";
        }
      }
    };

    // 초기 로드 시 한 번 실행 (약간 지연시켜 DOM이 완전히 렌더링된 후 실행)
    setTimeout(adjustToolbarHeight, 0);

    // throttle 구현 - 마지막 호출 후 100ms 내에는 재호출하지 않음
    let throttleTimeout = null;
    const throttledAdjust = () => {
      if (throttleTimeout === null) {
        throttleTimeout = setTimeout(() => {
          adjustToolbarHeight();
          throttleTimeout = null;
        }, 100);
      }
    };

    // ResizeObserver를 사용하여 editorContainer의 크기 변화 감지
    const resizeObserver = new ResizeObserver(() => {
      // throttle 적용된 함수 호출
      throttledAdjust();
    });

    resizeObserver.observe(editorContainer);

    // 창 크기 변경 시 이벤트 리스너 추가 (throttle 적용)
    window.addEventListener("resize", throttledAdjust);

    // 전역 스타일 적용
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      .ql-toolbar .ql-formats {
        margin-right: 14px !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      // 정리 코드
      if (quillInstance.current) {
        quillInstance.current.off("selection-change");
        quillInstance.current.off("text-change");
      }
      // ResizeObserver 해제
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      // 이벤트 리스너 제거
      window.removeEventListener("resize", throttledAdjust);
      // throttle timeout 정리
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      // 추가한 스타일 요소 제거
      document.head.removeChild(styleElement);
    };
  }, []);

  // initialHTML prop이 변경되었을 때만 내용 업데이트 (최초 마운트 시)
  useEffect(() => {
    if (quillInstance.current && initialHTML && !content) {
      quillInstance.current.clipboard.dangerouslyPasteHTML(initialHTML);
      setContent(initialHTML);
    }
  }, [initialHTML]);

  useEffect(() => {
    if (quillInstance.current) {
      quillInstance.current.enable(!readOnly);
    }
  }, [readOnly]);

  useImperativeHandle(ref, () => ({
    getLength: () => quillInstance.current?.getLength() || 0,
    getEditor: () => quillInstance.current,
    getHTML: () => {
      return quillInstance.current?.root.innerHTML || "";
    },
    // 현재 에디터 내용을 가져오는 메서드 추가
    getContent: () => content,
  }));

  // 스타일 추가 - 툴바 중복 방지 및 레이아웃 보정
  return (
    <div
      className="quill-editor-wrapper"
      ref={containerRef}
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        border: "none", // 이중 테두리 방지
      }}
    />
  );
});

export default Editor;
